import { filterPathIntersectionCached, sortForSortierung } from "oc-app/metadata/GraphMetadata";
import {
  elementOrTor,
  findAuswahl,
  getElementMetaCombinations,
  partToWorkflow,
} from "./elementFunctions";
import { agt, getAbschlussleistenAuswahlwerteForElemente } from "./Arrangement";
import * as Commons from "oc-commons/Commons";
import _ from "lodash";
import { ZElement } from "./ZElement";
import { MAuswahl, MAuswahlwert, MElement, MTor } from "../../metamodell";
import { PAuswahlwert, PDetailSerie, PElement, PSerie, PTor } from "../../metamodell_paths";

// Es gibt das Problem, welche Auswahlenwerte werden dem User zur Auswahl angeboten?
// Für den Fall eines Tores gibt es z.B. verschiedene Rahmenfarben, aber nicht alle Rahmenfarben
// sind möglich für Tore einer bestimmten Füllung. So ist z.B. weiß nicht möglich für ein WPC Platinum XL Grau Tor,
// wohl aber für andere Füllungen.
// Idee: Es wird eine Reihenfolge definiert, in der Auswahlen voneinander abhängen.
// Die Form sollte  unabhängig von allen anderen Auswahlen sein, d.h. andere Auswahlen ändern die Form nicht.
// Die Füllung sollte abhängig von der Form sein, d.h. beim Ändern der Form kann die Füllung sich automatisch ändern.
// Die Rahmenfarbe wiederum ist von der Form und Füllung abhängig.
// Um jetzt die möglichen Auswahlen zu bestimmen, werden die Auswahlen in dieser definierten Reihenfolge durchlaufen.
// Für eine Auswahl mit Index i wird die Menge aller Elemente bestimmt, welche die vorherigen
// festgelegten Auswahlen [1 .. i-1] erfüllen, und dann für diese Menge von Elementen für diese Auswahl i die Menge der
// möglichen Auswahlwerte bestimmt. Wenn der User dann einen Auswahlwert ändert, dann ändern sich potentiell
// die Auswahlwerte aller abhängigen Auswahlen ebenfalls.
// Ein alternatives, aber komplexeres Modell ist, dass auch die Auswahlwerte angeboten werden, welche durch die
// vorherigen Auswahlen eigentlich nicht möglich sind, und bei deren Anwahl auch die vorherigen Auswahlen umspringen.
// Man könnte das dadurch signalisieren, indem diese Auswahlwerte ausgegraut angezeigt werden.
// Eine Reihenfolge erscheint aber in jedem Fall sinnvoll, die Form ist eben grundlegender als die Füllung.
// Die Implementierung könnte insofern vereinfacht werden, als dass man diese Logik für alle Auswahlen an einer
// Stelle zusammenfasst, und eine Liste von Auswahl-Objekten definiert. Die Liste gibt die Abhängigkeits-Reihenfolge an.
// Die Menge aller möglichen Elemente "fließt" durch diese Liste und wird immer weiter gefiltert.
// Jedes Auswahl-Objekt hat eine Funktion, um die Menge aller möglichen Auswahlwerte aus dieser Elementmenge zu extrahieren,
// und eine Funktion, um einen Auswahlwert auf eine Elementinstanz zu setzen, und natürlich einen gewählten Auswahlwert.

export type ElementAuswahlAccessorKey =
  | "EasyMass"
  | "Füllung"
  | "Rahmen"
  | "Profilkategorie"
  | "Profil"
  | "Profilleiste"
  | "Abschlussleiste"
  | "Aufsatzleiste"
  | "Gehflügel"
  | "Füllungsseite"
  | "E_Antrieb_Vorhanden"
  | "Anschlag"
  | "Schieberichtung"
  | "Schiebetormotorseite"
  | "Drücker"
  | "Profilzylinder"
  | "Toröffner"
  | "Torschließer"
  | "Öffnung";

/**
 * Finale Auswahlaccessoren, die im Arrangement verfügbar sind, um Auswahlwerte abzufragen oder zu setzen
 */
export interface ElementAuswahlAccessor {
  key: ElementAuswahlAccessorKey;
  auswahl: MAuswahl;
  auswahlwert: MAuswahlwert;
  möglicheAuswahlwerte: MAuswahlwert[];
  setter?: (auswahlwert: MAuswahlwert) => void;
}

// Typdefinition für die Logik eines Steps
type StepLogic = {
  // Return type geändert: undefined signalisiert "keine Logik vorhanden", [] signalisiert "keine Treffer"
  getOptions: (metas: MTor[], element: ZElement) => MAuswahlwert[] | undefined;
  getValue: (element: ZElement) => MAuswahlwert;
  setValue: (elemente: ZElement[], val: MAuswahlwert) => void;
  filterMetas?: (metas: MTor[], element: ZElement, currentVal: MAuswahlwert) => MTor[];
  auswahlKey?: string;
};

/**
 * Gibt zu einer Menge von Elementen die entsprechenden Auswahlaccessoren als Objekt-Map key->Accessor zurück.
 * Die Berechnung erfolgt prozedural Schritt für Schritt.
 */
export function filterElementAuswahlAccessorenForMany(
  elemente: ZElement[]
): Record<ElementAuswahlAccessorKey, ElementAuswahlAccessor> {
  // --- 1. Initialisierung des Contexts (State Vector) ---
  const contexts = elemente.map((element) => {
    const { DetailSerien, Workflow } = partToWorkflow(element);
    const { elementMetasMass: initialMetas } = getElementMetaCombinations({
      form: element.form,
      flügelSizes: element.getFlügelSizesV2(),
      füllungAuswahlwert: null,
    });
    return { element, metas: initialMetas as MTor[], DetailSerien, Workflow };
  });

  const results: ElementAuswahlAccessor[] = [];

  // --- 2. Helper Funktionen für den Ablauf ---

  /**
   * Führt einen Accessor-Schritt für alle Elemente aus, aggregiert das Ergebnis
   * und aktualisiert den Context (Metas) für den nächsten Schritt.
   */
  const process = (key: ElementAuswahlAccessorKey, logic: StepLogic) => {
    const { auswahlKey } = logic;
    const möglicheAuswahlwerteSet = new Set<MAuswahlwert>();
    // Wir sammeln Values nicht mehr für maxFrequent, aber das Array kann für Debugging nützlich bleiben
    // const values: MAuswahlwert[] = [];
    let commonAuswahl: MAuswahl | null = null;
    let differentAuswahlen = false;

    // Default Options Helper
    const getDefaultOptions = () =>
      APP.metadata.Auswahlwert.filter((w) => w.Auswahl.Key == (auswahlKey ?? key));

    if (contexts.length === 0) {
      // Spezialfall: Keine Elemente -> Nur Defaults
      getDefaultOptions().forEach((o) => möglicheAuswahlwerteSet.add(o));
      commonAuswahl = findAuswahl(auswahlKey ?? key);
    } else {
      contexts.forEach((ctx) => {
        // Metadaten-Objekt bestimmen
        const currentAuswahl = findAuswahl(auswahlKey ?? key, ctx.DetailSerien, ctx.Workflow);
        if (!commonAuswahl) {
          commonAuswahl = currentAuswahl;
        } else if (commonAuswahl !== currentAuswahl) {
          differentAuswahlen = true;
        }

        const val = logic.getValue(ctx.element);
        // State Update (Metas filtern)
        if (val && logic.filterMetas) {
          ctx.metas = logic.filterMetas(ctx.metas, ctx.element, val);
        }

        // Options berechnen
        const options = logic.getOptions(ctx.metas, ctx.element);

        // FIX: Unterscheidung zwischen undefined (keine Logik -> Default) und [] (leeres Ergebnis -> leer)
        const effectiveOptions = options !== undefined ? options : getDefaultOptions();
        effectiveOptions.forEach((o) => möglicheAuswahlwerteSet.add(o));
      });
    }

    if (differentAuswahlen) {
      commonAuswahl = findAuswahl(auswahlKey ?? key);
    }
    if (!commonAuswahl) {
      throw new Error(`missing Auswahl with key ${auswahlKey ?? key} from db`);
    }

    // FIX: Verhalten wiederhergestellt - der Wert des letzten Elements gewinnt (Last Write Wins)
    // Dies entspricht dem Verhalten von array.reduce(...) im Originalcode.
    const lastElement = elemente.length > 0 ? elemente[elemente.length - 1] : undefined;
    const auswahlwert = lastElement ? logic.getValue(lastElement) : undefined;

    let möglicheAuswahlwerte = Array.from(möglicheAuswahlwerteSet);
    möglicheAuswahlwerte = sortForSortierung(möglicheAuswahlwerte);

    results.push({
      key,
      auswahl: commonAuswahl,
      auswahlwert, // Type cast might be needed if strictly MAuswahlwert, but undefined matches typical optional flow
      möglicheAuswahlwerte,
      setter: (auswahlwert) => logic.setValue(elemente, auswahlwert),
    });
  };

  /**
   * Helper für einfache Property-Mappings
   */
  const simple = (
    property: string,
    path: ((p: PTor) => PAuswahlwert) | null = null,
    chainFilter = true
  ): StepLogic => ({
    getValue: (element) => element[property],
    setValue: (elemente, val) => {
      elemente.forEach((element) => {
        const setter = () => (element[property] = val);
        if (element.isTor) {
          element.behalteLichteweite(setter);
        } else {
          setter();
        }
      });
      APP.dirty();
    },
    getOptions: (metas, element) => {
      if (path) {
        return filterPathIntersectionCached("Simple:" + property)(metas, path);
      }
      // FIX: Rückgabe von undefined, damit process() weiß, dass es auf Defaults zurückfallen soll
      return undefined;
    },
    filterMetas: (metas, element, currentVal) => {
      if (!chainFilter || !path || !currentVal) return metas;
      return metas.filter((meta) => {
        const values = filterPathIntersectionCached("Filter:" + property)([meta], path);
        return values.includes(currentVal);
      });
    },
  });

  // --- 3. Der Prozedurale Ablauf ---

  // 1. Maße
  process("EasyMass", {
    getOptions: (metas) =>
      filterPathIntersectionCached("AuswahlAccessoren.js:47")(
        agt().serie,
        (p: PSerie) => elementOrTor(p.DetailSerie).Mass.EasyAuswahlwert
      ) as MAuswahlwert[],
    getValue: () => agt().massAuswahlwert,
    setValue: (elemente, val) => agt().changeSelectedZaunElemente({ massAuswahlwert: val }),
  });

  // 2. Füllung
  process("Füllung", {
    getOptions: (elementMetas) =>
      filterPathIntersectionCached("Arrangement.js:2347")(
        elementMetas,
        (p: PTor) => elementOrTor(p.DetailSerie.Serie.DetailSerie).Füllung.Auswahlwert,
        elementMetas,
        (p: PTor) => elementOrTor(p.Mass.Form.Mass).Füllung.Auswahlwert,
        agt().serie.DetailSerie,
        (p: PDetailSerie) => elementOrTor(p).Füllung.Auswahlwert
      ) as MAuswahlwert[],
    filterMetas: (metas, element, currentVal) => {
      const path = (p: PTor) => p.Füllung.Auswahlwert;
      return metas.filter((meta) => {
        const values = filterPathIntersectionCached("Filter:Füllung")([meta], path);
        return values.includes(currentVal);
      });
    },
    getValue: () => agt().füllungAuswahlwert,
    setValue: (elemente, val) => agt().changeSelectedZaunElemente({ füllungAuswahlwert: val }),
  });

  // 3. Rahmen
  process(
    "Rahmen",
    simple("rahmenAuswahlwert", (p: PElement) => p.Rahmen.Auswahlwert)
  );

  // 4. Profilkategorie
  process("Profilkategorie", {
    auswahlKey: "Profilkategorie",
    getOptions: (elementMetas) =>
      filterPathIntersectionCached("ElementAuswahlAccessoren.ts:231")(
        elementMetas,
        (p: PTor) => p.Profilkategorie.Auswahlwert
      ) as MAuswahlwert[],
    getValue: () =>
      agt().selectedObjects[0]?.isElement && agt().selectedObjects[0]?.profilKategorieAuswahlwert,
    setValue: (elemente, val) => {
      agt()
        .getSelectedOrAllElemente()
        .forEach((e) => (e.profilKategorieAuswahlwert = val));
      APP.dirty();
    },
  });

  // 5. Profil
  process("Profil", {
    ...simple("profilAuswahlwert", (p: PTor) => p.Profilkategorie.Profile.Auswahlwert),
    auswahlKey: "Füllung",
    getValue: () =>
      agt().selectedObjects[0]?.isElement && agt().selectedObjects[0]?.profilAuswahlwert,
    setValue: (elemente, val) => {
      agt().changeSelectedZaunElemente({ profilAuswahlwert: val });
      APP.dirty();
    },
  });

  // 6. Profilleiste
  process("Profilleiste", {
    ...simple(
      "profilleisteAuswahlwert",
      (p: PTor) =>
        p.Profilkategorie.proxyUnion(
          (p) => p.Profilleiste,
          (p) => p.ProfilleisteOben
        ).Auswahlwert
    ),
    getValue: (element) => element?.isElement && element?.profilleisteAuswahlwert,
    setValue: (elemente, val) => {
      elemente.forEach((e) => (e.profilleisteAuswahlwert = val));
      APP.dirty();
    },
  });

  // 7. Abschlussleiste
  process("Abschlussleiste", {
    auswahlKey: "AbschlussleisteOben",
    getOptions: (elementMetas, element) =>
      getAbschlussleistenAuswahlwerteForElemente(
        elementMetas.map((meta) => ({ meta, size: element.size }))
      ),
    getValue: () =>
      agt().selectedObjects[0]?.isElement && agt().selectedObjects[0]?.abschlussleisteAuswahlwert,
    setValue: (elemente, val) => {
      const individualisiert = agt().selectedObjects.filter((o) => o.isElement).length > 0;
      elemente.forEach((element) => {
        if (
          individualisiert ||
          !element.abschlussleisteAuswahlwertIndividualisiert ||
          agt().isAllPartsIndividualisiert(
            agt().getElemente(),
            "abschlussleisteAuswahlwertIndividualisiert"
          )
        ) {
          element.abschlussleisteAuswahlwert = val;
          if (individualisiert) {
            element.abschlussleisteAuswahlwertIndividualisiert =
              agt().abschlussleisteAuswahlwert_ != val;
            element.showIndividualisiert = true;
            agt().recentlyIndividualisiertAuswahlwert = "abschlussleisteAuswahlwert";
            agt().showAuswahlwertIndividualisiertInfo([val]);
          }
        }
      });
      APP.dirty();
    },
  });

  // 8. Aufsatzleiste
  process(
    "Aufsatzleiste",
    simple("aufsatzleisteAuswahlwert", (p: PTor) => p.Aufsatzleiste.Auswahlwert)
  );

  // 9. Gehflügel
  process(
    "Gehflügel",
    simple("gehflügelAuswahlwert", (p: PTor) => p.Gehflügel.Auswahlwert, false)
  );

  // 10. Füllungsseite
  process(
    "Füllungsseite",
    simple("füllungsseiteAuswahlwert", (p: PTor) => p.Füllungsseite.Auswahlwert, false)
  );

  // 11. E_Antrieb_Vorhanden
  process("E_Antrieb_Vorhanden", {
    getOptions: (elementMetas, element) =>
      elementMetas.flatMap((elementMeta) =>
        elementMeta.Mass.EAntriebAuswahlMöglich && element.isEAntriebMöglich
          ? [
              APP.richMetadata.findAuswahlwert("E_Antrieb_mit"),
              APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"),
            ]
          : APP.richMetadata.findAuswahlwert("E_Antrieb_ohne")
      ),
    filterMetas: (metas, element, currentVal) => {
      return metas.filter((meta) => {
        const val = meta.E_Antrieb_Vorhanden
          ? APP.richMetadata.findAuswahlwert("E_Antrieb_mit")
          : APP.richMetadata.findAuswahlwert("E_Antrieb_ohne");
        return val === currentVal;
      });
    },
    getValue: (element) => element.eAntriebAuswahlwert,
    setValue: (elemente, val) => {
      elemente.forEach((element) => {
        const setter = () => (element.eAntriebAuswahlwert = val);
        if (element.isTor) {
          element.behalteLichteweite(setter);
        } else {
          setter();
        }
      });
      APP.dirty();
    },
  });

  // 12. Anschlag
  process("Anschlag", simple("anschlagAuswahlwert"));

  // 13. Schieberichtung
  process("Schieberichtung", simple("schieberichtungAuswahlwert"));

  // 14. Schiebetormotorseite
  process("Schiebetormotorseite", simple("schiebetormotorseiteAuswahlwert"));

  // 15. Drücker
  process(
    "Drücker",
    simple("drückerAuswahlwert", (p: PTor) => p.Drücker.Auswahlwert)
  );

  // 16. Profilzylinder
  process(
    "Profilzylinder",
    simple("profilzylinderAuswahlwert", (p: PTor) => p.Profilzylinder.Auswahlwert)
  );

  // 17. Toröffner
  process("Toröffner", {
    getOptions: (elementMetas, element) =>
      filterPathIntersectionCached("AuswahlAccessoren.js:282")(
        (element?.isToröffnerMöglich ? elementMetas : []) as MTor[],
        (m: PTor) => m.Toröffner.Auswahlwert
      ),
    filterMetas: (metas, element, currentVal) => {
      const path = (m: PTor) => m.Toröffner.Auswahlwert;
      return metas.filter((meta) => {
        const values = filterPathIntersectionCached("Filter:Toröffner")([meta], path);
        return values.includes(currentVal);
      });
    },
    getValue: (element) => element.toröffnerAuswahlwert,
    setValue: (elemente, val) => {
      elemente.forEach((element) => {
        const setter = () => (element.toröffnerAuswahlwert = val);
        if (element.isTor) {
          element.behalteLichteweite(setter);
        } else {
          setter();
        }
      });
      APP.dirty();
    },
  });

  // 18. Torschließer
  process("Torschließer", {
    getOptions: (elementMetas, element) =>
      filterPathIntersectionCached("AuswahlAccessoren.js:293")(
        element?.isTorschließerMöglich ? elementMetas : [],
        (m: PTor) => m.Torschließer.Auswahlwert
      ),
    filterMetas: (metas, element, currentVal) => {
      const path = (m: PTor) => m.Torschließer.Auswahlwert;
      return metas.filter((meta) => {
        const values = filterPathIntersectionCached("Filter:Torschließer")([meta], path);
        return values.includes(currentVal);
      });
    },
    getValue: (element) => element.torschließerAuswahlwert,
    setValue: (elemente, val) => {
      elemente.forEach((element) => {
        const setter = () => (element.torschließerAuswahlwert = val);
        if (element.isTor) {
          element.behalteLichteweite(setter);
        } else {
          setter();
        }
      });
      APP.dirty();
    },
  });

  // 19. Öffnung
  process("Öffnung", {
    ...simple("öffnungAuswahlwert", (p: PTor) => p.Öffnung.Auswahlwert),
    setValue: (elemente, val) => {
      const tore = elemente.filter((o) => o.isTor);
      tore.forEach((e) => {
        if (e.isDoppeltor && e.öffnungAuswahlwert !== val) {
          const gehflügelAuswahlwert = APP.richMetadata.findAuswahlwert(
            e.isGehflügelLinks ? "Gehflügel_rechts" : "Gehflügel_links"
          );
          e.gehflügelAuswahlwert = gehflügelAuswahlwert;
        }
        e.öffnungAuswahlwert = val;
      });
      APP.dirty();
    },
  });

  return _.keyBy(results, "key") as Record<ElementAuswahlAccessorKey, ElementAuswahlAccessor>;
}
